import { useCallback, useLayoutEffect, useState } from 'react'

const identity = (width) => width

// Shared measuring core. `select` maps the raw width to the value kept in state;
// React bails out of a render when that value is unchanged, so a coarse
// `select` (a breakpoint band) only re-renders the owner when a band is crossed.
const useMeasuredWidth = (select) => {
    const [node, setNode] = useState(null)
    const [value, setValue] = useState(() => select(0))
    const ref = useCallback((el) => setNode(el), [])

    useLayoutEffect(() => {
        if (!node) return undefined
        const update = () => setValue(select(node.clientWidth))
        update()

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update)
            return () => window.removeEventListener('resize', update)
        }

        const observer = new ResizeObserver(update)
        observer.observe(node)
        return () => observer.disconnect()
        // `select` is expected to be stable (module-level or memoised).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node])

    return [ref, value]
}

/**
 * Measures the rendered width of a DOM node and keeps it current across
 * resizes. Returns a callback ref so the measurement re-runs whenever the
 * node mounts or unmounts (for example when a tab switch swaps the element).
 *
 * @example
 *   const [ref, width] = useElementWidth()
 *   <div ref={ref} />
 *
 * @returns {[(el: HTMLElement | null) => void, number]} `[ref, width]`.
 *   `width` is 0 until the node has been measured.
 */
export const useElementWidth = () => useMeasuredWidth(identity)

/**
 * Like useElementWidth, but reports only which band the width falls in, so the
 * owner re-renders when a breakpoint is crossed rather than on every pixel of a
 * resize. Use it when layout depends on the space a node actually gets (e.g. a
 * page beside a collapsible sidebar), which viewport media queries cannot see.
 *
 * @param {(width: number) => string} toBand stable function from width to band
 *   name. It also receives 0 before the first measurement.
 *
 * @example
 *   const band = (w) => (w && w < 1000 ? 'narrow' : 'wide')
 *   const [ref, size] = useElementWidthBand(band)
 */
export const useElementWidthBand = (toBand) => useMeasuredWidth(toBand)

export default useElementWidth
